import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Mail, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const errMsg = (e) => e?.response?.data?.detail || "Erreur, réessayez";

export function SecurityBastionCard() {
  const { refresh } = useAuth();
  const [sec, setSec] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => http.get("/me/security").then((r) => setSec(r.data)).catch(() => setSec(null));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy("setup");
    try { setSetup((await http.post("/me/totp/setup")).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const confirm = async () => {
    setBusy("confirm");
    try {
      await http.post("/me/totp/confirm", { code });
      toast.success("Authentificateur activé");
      setSetup(null); setCode(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!sec) return null;
  return (
    <div className="mt-6 glass-card p-7" data-testid="security-bastion-card">
      <div className="font-bold text-lg mb-1 inline-flex items-center gap-2"><ShieldCheck className="text-emerald-600" size={20} /> Sécurité du compte</div>
      <p className="text-sm text-[var(--ink-soft)] mb-4">
        {sec.totp_required ? "Compte administrateur : l'authentificateur est obligatoire pour changer le mot de passe ou l'email." : "Protégez votre compte avec une application d'authentification (Google Authenticator, Authy…)."}
      </p>

      {sec.totp_enabled ? (
        <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full" data-testid="totp-enabled-badge">
          <ShieldCheck size={14} /> Authentificateur actif
        </div>
      ) : setup ? (
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 items-start" data-testid="totp-setup">
          <div className="bg-white p-3 rounded-2xl border border-[var(--line)] w-fit"><QRCodeSVG value={setup.otpauth_uri} size={160} /></div>
          <div className="text-sm space-y-3">
            <p>1. Scannez ce QR code avec Google Authenticator (ou saisissez la clé : <code className="font-mono text-xs bg-orange-50 px-1 py-0.5 rounded" data-testid="totp-secret">{setup.secret}</code>).</p>
            <p>2. Entrez le code à 6 chiffres affiché :</p>
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="123456" className="!w-32 font-mono tracking-widest" data-testid="totp-confirm-code" />
              <button onClick={confirm} disabled={busy === "confirm" || code.length < 6} className="btn-primary disabled:opacity-50" data-testid="totp-confirm-btn">
                {busy === "confirm" ? <Loader2 size={14} className="animate-spin" /> : "Activer"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button onClick={startSetup} disabled={busy === "setup"} className="btn-primary inline-flex items-center gap-2" data-testid="totp-setup-btn">
          <KeyRound size={14} /> Activer l'authentificateur
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <PasswordForm needCode={sec.totp_enabled} locked={sec.totp_required && !sec.totp_enabled} />
        <EmailForm needCode={sec.totp_enabled} locked={sec.totp_required && !sec.totp_enabled} current={sec.email} onDone={async () => { await refresh(); load(); }} />
      </div>
    </div>
  );
}

function PasswordForm({ needCode, locked }) {
  const [f, setF] = useState({ current_password: "", new_password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await http.post("/me/password", f);
      toast.success("Mot de passe modifié — vos autres sessions ont été déconnectées");
      setF({ current_password: "", new_password: "", code: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="password-change-form">
      <div className="font-semibold text-sm flex items-center gap-2 mb-2"><KeyRound size={14} /> Changer le mot de passe</div>
      <input type="password" required placeholder="Mot de passe actuel" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="pw-current" />
      <input type="password" required minLength={12} placeholder="Nouveau mot de passe (12 caractères min.)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="pw-new" />
      {needCode && <input inputMode="numeric" maxLength={6} required placeholder="Code authentificateur" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="pw-totp" />}
      <button type="submit" disabled={busy || locked} className="btn-secondary w-full disabled:opacity-50" data-testid="pw-submit">{locked ? "Activez d'abord l'authentificateur" : "Modifier"}</button>
    </form>
  );
}

function EmailForm({ needCode, locked, current, onDone }) {
  const [f, setF] = useState({ new_email: "", current_password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await http.post("/me/email", f);
      toast.success(`Email de connexion changé : ${r.data.email}`);
      setF({ new_email: "", current_password: "", code: "" }); onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="email-change-form">
      <div className="font-semibold text-sm flex items-center gap-2 mb-2"><Mail size={14} /> Changer l'email de connexion <span className="text-[var(--ink-soft)] font-normal">({current})</span></div>
      <input type="email" required placeholder="Nouvel email" value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} data-testid="em-new" />
      <input type="password" required placeholder="Mot de passe actuel" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="em-password" />
      {needCode && <input inputMode="numeric" maxLength={6} required placeholder="Code authentificateur" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="em-totp" />}
      <button type="submit" disabled={busy || locked} className="btn-secondary w-full disabled:opacity-50" data-testid="em-submit">{locked ? "Activez d'abord l'authentificateur" : "Modifier"}</button>
    </form>
  );
}
